import { Button, Flex, Image } from "rebass/styled-components";
import styled from "styled-components";
import Countdown from "react-countdown";
import { Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import { IoClose } from "react-icons/io5";
import { useEffect, useState } from "react";

import { EnterCOTDWidgetViewModel } from "./ViewModel";
import { SidebarExpandedState } from "MVVM/State/sidebar";
import { CountDownRenderer } from "MVVM/Views/Competitions/Views/Play/countdownTimer";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipSelector } from "./Components/ClipSelector";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";

export const EnterCOTDWidget = () => {
  const { allstarUser } = useUser();
  const { data } = EnterCOTDWidgetViewModel();
  const [dismissed, setDismissed] = useState(false);
  const {
    portalOpen: clipSelectorOpen,
    setPortalOpen: setClipSelectorOpen,
    Portal: ClipSelectorPortal,
  } = usePortal(
    <ClipSelector
      campaign={data}
      closeModal={() => {
        setClipSelectorOpen(false);
      }}
    />,
    "root",
  );

  const sidebarOpen = useRecoilValue(SidebarExpandedState);

  useEffect(() => {
    if (data && allstarUser?.user?._id) {
      analytics.track("Component Viewed", {
        locationIdentifier: "cotd_widget",
        location: "side_rail",
        campaign: data?.campaign,
        featureGroup: "cotd_widget",
      });
    }
  }, [data, allstarUser?.user?._id]);

  useEffect(() => {
    if (!data?.campaign) return;
    setDismissed(
      !!window.localStorage.getItem(`dismissed_cotd_${data.campaign}`),
    );
  }, [setDismissed, data?.campaign]);

  if (!data || !allstarUser?.user?._id || dismissed) return null;

  const handleDismiss = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "cotd_widget_close",
      location: "side_rail",
      featureGroup: "cotd_widget",
      campaign: data?.campaign,
    });
    window.localStorage.setItem(`dismissed_cotd_${data.campaign}`, "true");
    setDismissed(true);
  };

  return (
    <Container
      backgroundImage={data?.images?.carouselBackground}
      sidebarOpen={sidebarOpen}
    >
      <CloseButton onClick={handleDismiss}>
        <IoClose size={24} />
      </CloseButton>
      {clipSelectorOpen && ClipSelectorPortal}
      <CampaignImage src={data?.images?.campaign} />
      <Countdown
        date={new Date(parseInt(data.campaignEnd))}
        renderer={(props) => (
          <CountDownRenderer
            days={props.days}
            hours={props.hours}
            minutes={props.minutes}
            seconds={props.seconds}
            color={data.color}
            variant="text.labelMedium"
            showEndsIn={false}
            showIcon={true}
          />
        )}
      />
      <CampaignButton
        color={data.color}
        secondaryColor={data.secondaryColor}
        onClick={() => {
          analytics.track("Button - Click", {
            locationIdentifier: "choose_my_cotd",
            location: "side_rail",
            featureGroup: "cotd_widget",
            campaign: data?.campaign,
          });
          setClipSelectorOpen(true);
        }}
      >
        <Text
          color={data.secondaryColor}
          variant="text.paragraphSmall"
          style={{ fontWeight: 500 }}
        >
          Choose My COTD
        </Text>
      </CampaignButton>
    </Container>
  );
};

const CloseButton = styled(Button)`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  opacity: 0.7;
  padding: 4px;
  position: absolute;
  right: 0px;
  top: 0px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const CampaignButton = styled(Button)<{
  color: string;
  secondaryColor: string;
}>`
  background-color: ${({ color }) => color};
  border-radius: 8px;
  border: 2px solid ${({ secondaryColor }) => secondaryColor};
  font-size: 16px;
  font-weight: 600;
  padding: 12px;
  width: 100%;

  &:hover {
    background-color: ${({ color }) => color}dd;
    cursor: pointer;
  }
`;

const CampaignImage = styled(Image)`
  height: auto;
  width: 125px;
`;

const Container = styled(Flex)<{
  backgroundImage: string;
  sidebarOpen: boolean;
}>`
  align-items: center;
  background-image: url(${({ backgroundImage }) => backgroundImage});
  background-position: center;
  background-size: cover;
  border-radius: 8px;
  border: 4px solid ${({ theme }) => theme.colors.darkerNed};
  bottom: 20px;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  left: 20px;
  padding: 12px;
  position: absolute;
  width: 200px;
  z-index: 10;
  ${({ sidebarOpen }) =>
    !sidebarOpen && "box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);"}
`;
